'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MessageSquare, AlertTriangle } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from '@/shared/ui/shadcn/drawer';
import { cn } from '@/shared/lib/cn';
import type { Render } from '@/types/render';

interface LlmFeedbackImageOverlayProps {
  render: Render | null | undefined;
  /**
   * Pulse trigger for the feedback icon — any new non-null value (the id of a
   * render/job that just completed on this screen) starts a grow/shrink pulse
   * that pulls the user's eye to the fresh AI message. The overlay latches the
   * signal internally and stops on its own: a click or a 5 s timeout — so the
   * pulse survives the trigger value disappearing (job pruned from the queue).
   *
   * Priority: when BOTH icons are present, ONLY the negative (filtered) icon
   * pulses — a content warning is more important to surface than praise. The
   * positive icon pulses only when there's no negative one.
   */
  pulseSignal?: string | null;
  className?: string;
}

type FeedbackDetail = {
  variant: 'info' | 'warning';
  title: string;
  message: ReactNode;
};

/**
 * LLM feedback (positive + filtered/negative) for a render, shown as small
 * icons pinned to the bottom-center border of the angle image — tapping one
 * opens a modal with the full text. Mirrors Apple's `LlmFeedbackIconBar` +
 * `LlmFeedbackDetailSheet`: the image area stays clean (no inline banners)
 * while the feedback is still one tap away. Renders nothing when there's no
 * feedback. Cross-platform parity — used in BOTH arrangements.
 */
export function LlmFeedbackImageOverlay({
  render,
  pulseSignal,
  className,
}: LlmFeedbackImageOverlayProps) {
  const [detail, setDetail] = useState<FeedbackDetail | null>(null);

  // Attention pulse on the positive icon. Latched from `pulseSignal` so the
  // pulse keeps running its full 4 s window even after the transient job entry
  // is pruned from the queue store; a click stops it earlier.
  const [isPulsing, setIsPulsing] = useState(false);
  const seenPulseSignal = useRef<string | null>(null);
  const pulseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPulse = () => {
    if (pulseTimer.current) clearTimeout(pulseTimer.current);
    pulseTimer.current = null;
    setIsPulsing(false);
  };

  useEffect(() => {
    if (!pulseSignal || pulseSignal === seenPulseSignal.current) return;
    seenPulseSignal.current = pulseSignal;
    setIsPulsing(true);
    if (pulseTimer.current) clearTimeout(pulseTimer.current);
    pulseTimer.current = setTimeout(() => setIsPulsing(false), 5000);
  }, [pulseSignal]);

  // Unmount-only cleanup — intentionally NOT keyed on pulseSignal, so the
  // 5 s window isn't cut short when the signal value changes or clears.
  useEffect(
    () => () => {
      if (pulseTimer.current) clearTimeout(pulseTimer.current);
    },
    []
  );

  const positive = render?.attributes?.llm_feedback?.positive_feedback;
  const filtered = render?.attributes?.llm_feedback?.filtered_content;
  const hasPositive = !!(positive && positive.trim() !== '');
  const hasFiltered = !!(filtered && filtered.length > 0);

  if (!hasPositive && !hasFiltered) return null;

  // Single attention pulse, routed by priority: the negative (filtered) icon
  // wins when both are present — a content warning matters more than praise.
  const pulseNegative = isPulsing && hasFiltered;
  const pulsePositive = isPulsing && hasPositive && !hasFiltered;

  const filteredText = hasFiltered ? filtered!.join(' • ') : '';
  const filteredMessage: ReactNode = hasFiltered
    ? filtered!.length === 1
      ? filtered![0]
      : (
          <ul className="space-y-1">
            {filtered!.map((item, i) => (
              <li key={i} className="flex items-start">
                <span className="mr-2">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )
    : null;

  return (
    <>
      {/* Bare tinted glyphs (no circular backing) — 44px hit area each; the
          teal / amber tint identifies the variant. */}
      <div className={cn('flex items-center', className)}>
        {hasPositive && (
          <button
            type="button"
            aria-label={positive!}
            onClick={() => {
              stopPulse();
              setDetail({
                variant: 'info',
                title: positive!,
                message: positive!,
              });
            }}
            className="inline-flex h-11 w-11 items-center justify-center text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.45)] transition-opacity hover:opacity-80"
          >
            {/* Speech-bubble glyph — same icon as the "What to change" prompt
                row, so it reads as "the AI left you a message", not generic
                info. Pulse (grow/shrink) announces a just-completed render —
                only when there's no negative icon to take priority. */}
            <MessageSquare
              fill="currentColor"
              className={cn('h-5 w-5', pulsePositive && 'animate-feedback-pulse')}
            />
          </button>
        )}
        {hasFiltered && (
          <button
            type="button"
            aria-label={filteredText}
            onClick={() => {
              stopPulse();
              setDetail({
                variant: 'warning',
                title: filteredText,
                message: filteredMessage,
              });
            }}
            className="inline-flex h-11 w-11 items-center justify-center text-amber-600 dark:text-amber-400 transition-opacity hover:opacity-80"
          >
            {/* Pulse (grow/shrink) announces a just-completed render and takes
                priority over the positive icon — a content warning matters
                more than praise. */}
            <AlertTriangle
              className={cn('h-5 w-5', pulseNegative && 'animate-feedback-pulse')}
            />
          </button>
        )}
      </div>

      {/* Bottom sheet (not a centered dialog) — matches iOS's tinted sheet and
          the project rule that WEB leans iOS. Tinted by variant using the same
          tokens as NoticeBanner: #FFF8E1 warm amber / #F0FEFE light teal.
          Dismissed by drag-down / overlay click, so no explicit Close button
          for this read-only text. */}
      <Drawer open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DrawerContent
          className={cn(
            'border-0 px-5 pb-8',
            detail?.variant === 'warning'
              ? 'bg-[#FFF8E1] dark:bg-amber-900/20'
              : 'bg-[#F0FEFE] dark:bg-teal-900/20'
          )}
        >
          {/* Title/description carry the feedback text itself (server-driven,
              already localized) so the sheet needs no new translation key. */}
          <DrawerTitle className="sr-only">{detail?.title ?? ''}</DrawerTitle>
          <DrawerDescription className="sr-only">
            {detail?.title ?? ''}
          </DrawerDescription>
          {detail && (
            <div className="mx-auto flex w-full max-w-md items-start gap-3 pt-2">
              {detail.variant === 'warning' ? (
                <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-amber-600 dark:text-amber-400" />
              ) : (
                <MessageSquare fill="currentColor" className="mt-0.5 h-6 w-6 shrink-0 text-[#078D86] dark:text-teal-400" />
              )}
              {/* Normal body role (16px / 1.5) for comfortable reading. */}
              <div className="text-card-body text-foreground">
                {detail.message}
              </div>
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
}
